import React from "react";
import ConstConfig from "../../utility/configs/constConfig";
import ImageConfig from "../../utility/configs/imageConfig";

/**
 * UserAvatar Component
 *
 * Displays user profile image with fallback to first letter
 */
const UserAvatar = ({
  profileImage,
  firstName = "",
  lastName = "",
  size = "lg",
  className = "",
  alt = "",
  style = {},
}) => {
  // Check if profile image exists and is valid
  const hasProfileImage =
    profileImage &&
    typeof profileImage === "string" &&
    profileImage.trim() !== "";

  // Map common className to explicit dimensions from CSS
  const getDimensions = () => {
    if (className.includes("imgDoc")) {
      return { width: "5em", height: "5em" };
    }
    if (className.includes("thimg")) {
      return { width: "15em", height: "15em" };
    }
    if (className.includes("avatar-lg")) {
      return { width: "40px", height: "40px" }; // Reduced for chat
    }
    if (className.includes("img-fluid")) {
      return { width: "100%", height: "260px" };
    }
    return { width: "5em", height: "5em" };
  };

  const dimensions = getDimensions();

  // If image exists, show img tag
  if (hasProfileImage) {
    return (
      <img
        src={`${ConstConfig.IMAGE_URL}/${profileImage}`}
        alt={alt || `${firstName} ${lastName}`.trim()}
        className={className}
        style={{
          objectFit: "cover",
          verticalAlign: "middle",
          ...style,
        }}
      />
    );
  }

  // Get first letter
  const getInitial = () => {
    if (firstName && typeof firstName === "string" && firstName.trim() !== "") {
      return firstName.trim().charAt(0).toUpperCase();
    }
    if (lastName && typeof lastName === "string" && lastName.trim() !== "") {
      return lastName.trim().charAt(0).toUpperCase();
    }
    return "?";
  };

  // Font size based on dimensions
  const getFontSize = () => {
    if (className.includes("thimg")) return "80px";
    if (className.includes("imgDoc")) return "40px";
    if (className.includes("avatar-lg")) return "24px";
    if (className.includes("img-fluid")) return "120px";
    // For explicit small sizes (32px)
    if (style.width === "32px") return "16px";
    return "48px";
  };

  // Get border radius matching image styles
  const getBorderRadius = () => {
    if (className.includes("thimg")) return "10px";
    if (className.includes("imgDoc")) return "100px";
    if (className.includes("avatar-lg")) return "50%";
    if (className.includes("img-fluid")) return "4px";
    if (className.includes("rounded-circle")) return "50%";
    return "4px";
  };

  // Get margin bottom matching image styles
  const getMarginBottom = () => {
    // Only apply margin to img-fluid that doesn't have thimg class
    if (className.includes("img-fluid") && !className.includes("thimg")) {
      return "0.6em";
    }
    return "0";
  };

  // If no image, show light gray background with primary color letter with 3D effect
  return (
    <div
      className={className}
      style={{
        ...dimensions,
        ...style,
        display: "inline-block",
        position: "relative",
        backgroundColor: "#f5f5f5",
        overflow: "hidden",
        flexShrink: 0,
        borderRadius: getBorderRadius(),
        marginBottom: getMarginBottom(),
        verticalAlign: "middle",
      }}
      title={alt || `${firstName} ${lastName}`.trim()}
      aria-label={alt || `${firstName} ${lastName}`.trim()}
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          textAlign: "center",
          color: "#1d7e7c",
          fontSize: getFontSize(),
          fontWeight: "900",
          fontFamily: "Helvetica Neue, sans-serif",
          textTransform: "uppercase",
          lineHeight: 1,
          textShadow: `
          0px 1px 0px #145f5e,
          0px 2px 0px #145f5e,
          0px 3px 0px #0f4847,
          0px 4px 0px #0f4847,
          0px 5px 0px #0a3635,
          0px 6px 5px rgba(0, 0, 0, 0.3),
          0px 8px 10px rgba(0, 0, 0, 0.2),
          0px 10px 15px rgba(0, 0, 0, 0.15)
        `,
        }}
      >
        {getInitial()}
      </div>
    </div>
  );
};

export default UserAvatar;
